"use client";

import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { useTranslations } from "next-intl";
import { ProfileOutlined } from "@ant-design/icons";
import BlogSearch from "@/components/Blogs/BlogSeach.tsx";
import { Blog } from "@/lib/@types/api/blog";
import { Link } from "@/navigation.ts";
import FeatureBlogs from "./FeaturedBlogs";
import { useLocale } from "next-intl";

// 使用 TailwindCSS 的一些自定义样式来优化文章显示
export const StyledArticle = styled.article`
	/* 定义与全局无关的样式 */
	p {
		display: block;
		margin-block-start: 1em;
		margin-block-end: 1em;
		margin-inline-start: 0px;
		margin-inline-end: 0px;
		unicode-bidi: isolate;
	}

	h1 {
		color: #18181b;
		font-weight: 700;
		font-size: 28px;
		padding: 4px 0;
	}

	/* 列表样式 */
	ul {
		list-style-type: disc;
		margin-left: 20px;
		padding-left: 0;
	}

	li {
		margin-bottom: 10px;
	}

	strong {
		padding: 0 2px;
	}

	/* 标题样式 */
	h2 {
		color: #18181b;
		font-weight: 700;
		font-size: 24px;
		padding: 4px 0;
	}

	h3 {
		color: #18181b;
		font-weight: 700;
		font-size: 20px;
		padding: 4px 0;
	}

	h4 {
		color: #18181b;
		font-weight: 600;
		font-size: 16px;
		padding: 4px 0;
	}

	h5 {
		color: #18181b;
		font-weight: 600;
		font-size: 14px;
		padding: 4px 0;
	}

	h6 {
		color: #18181b;
		font-weight: 500;
		font-size: 13px;
		padding: 4px 0;
	}

	span {
		color: #18181b;
		font-weight: 400;
		font-size: 16px;
		padding: 4px 0;
	}

	/* 表格样式 */
	table {
		width: 100%;
		border-collapse: collapse;
		margin-top: 1em;
		margin-bottom: 1em;
	}

	th,
	td {
		border: 1px solid #dcdcdc;
		padding: 8px;
		text-align: left;
	}

	/* 表格内文字 */
	table p {
		margin: 0;
	}
	a {
		padding: 0 2px;
	}
	/* 添加其他必要的样式，确保不被全局样式覆盖 */
`;

const BlogArticle = ({ content, blogList }: { content: string; blogList: Blog.BlogListItem[] }) => {
	const [headings, setHeadings] = useState<string[]>([]);
	const [newContent, setNewContent] = useState<string>(content); // 用于存储修改后的内容

	// 提取文章的 H1-H5 标题并生成目录，同时更新 newContent
	useEffect(() => {
		const parser = new DOMParser();
		const doc = parser.parseFromString(content, "text/html");

		// 提取标题并生成 ID
		const foundHeadings = Array.from(doc.querySelectorAll("h1, h2, h3, h4, h5")).map((h, index) => {
			const headingText = h.textContent || "";
			const headingId = headingText.trim().toLowerCase().replace(/\s+/g, "-"); // 将空格转义为 "-"
			h.id = headingId; // 给每个标题元素设置 ID
			return headingText; // 返回标题文本以供目录使用
		});

		// 更新内容为带有 ID 的 HTML
		let updatedContent = new XMLSerializer().serializeToString(doc); // 将修改后的文档序列化为 HTML
		setNewContent(updatedContent); // 更新 newContent
		setHeadings(foundHeadings); // 更新目录
	}, [content]);

	const t = useTranslations();
	let locale = useLocale();

	return (
		<div className="relative flex justify-between !gap-5 py-8 max-md:flex-wrap">
			{/* 左侧文章内容 */}
			<StyledArticle
				className="animate__animated animate__fadeIn h-[100vh] flex-1 overflow-auto overflow-y-auto pr-[490px]"
				style={{
					scrollbarWidth: "none", // Firefox
					msOverflowStyle: "none", // IE 10+
				}}
			>
				{/* 渲染修改后的内容，并通过 dangerouslySetInnerHTML 来处理 */}
				<div
					className="blog-content"
					dangerouslySetInnerHTML={{
						__html: newContent, // 渲染修改后的内容
					}}
				/>
			</StyledArticle>
			{/*{isFixed&&<div className="w-[450px] "></div>}*/}

			{/* 右侧 Table of Contents (TOC) */}
			{/*<div className={` ${isFixed ? ' fixed top-[105px]  z-50 container w-full  flex justify-end' : ''}`}>*/}
			<div className={`absolute right-0 w-[450px] max-md:hidden`}>
				<nav className={`border-gray-450 my-4 h-fit rounded border bg-[#f9f9f9] p-4`}>
					<div className="mb-4 flex items-center justify-between">
						<h3 className="text-xl font-semibold">{t("blog.Contents")}</h3>
						<ProfileOutlined className="rounded-xl text-2xl text-gray-300" />
					</div>
					<ul className="space-y-2">
						{headings.map((heading, index) => (
							<li key={index} className="cursor-pointer text-sm hover:underline">
								<span className="mr-1">{index + 1}.</span>
								<a className="text-black" href={`#${heading.trim().toLowerCase().replace(/\s+/g, "-")}`}>
									{heading}
								</a>
							</li>
						))}
					</ul>
				</nav>
				<BlogSearch></BlogSearch>
				<FeatureBlogs locale={locale}></FeatureBlogs>
			</div>

			{/*</div>*/}
		</div>
	);
};
const RelatedBlog = ({ blogList }: { blogList: Blog.BlogListItem[] }) => {
	const t = useTranslations();

	return (
		<>
			<h3 className={"my-4 border-b border-b-gray-400 py-4 text-xl"}>{t("blog.RelatedBlogs")}</h3>
			<ul>
				{blogList &&
					blogList.map((blog) => {
						return (
							<li key={blog.blog_id} className="border-b border-b-gray-400">
								<Link href={"/blog/" + blog.blog_slug}>
									<h3 className="my-2 line-clamp-2 !text-xl font-bold text-black">{blog.blog_title}</h3>
									{blog.blog_classification_list &&
										blog.blog_classification_list.map((item, index) => {
											return (
												index === 0 && (
													<div className="py-4 text-xs uppercase text-main" key={item.cls_slug}>
														{item.cls_name}
													</div>
												)
											);
										})}{" "}
								</Link>
							</li>
						);
					})}
			</ul>
		</>
	);
};

export default BlogArticle;
